import { useState, useEffect } from "react";
import LoginPopUp from "./LoginPopUp";
import SignUpPopUp from "./SignUpPopUp";
import UploadVideoPopUp from "./UploadVideoPopUp";
import ChatButton from "../Chat/ChatButton";
import ChatModal from "../Chat/ChatModal";
import { useAuth } from "../../firebase/authContext";
import ProfileIcon from "./ProfileIcon";
import { useNavigate, Link } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { FaSearch, FaPlus, FaTimes } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";

const Navbar = ({ toggleSidebar, showLoginPopup, setShowLoginPopup }) => {
  const [showSignUpPopUp, setShowSignUpPopUp] = useState(false);
  const [showUploadPopUp, setShowUploadPopUp] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false); // Mobile menu state

  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.length > 0) {
      const results = users.filter(
        (user) =>
          user.username &&
          user.username.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(results.slice(0, 5));
    } else {
      setFilteredUsers([]);
    }
  }, [searchQuery, users]);

  const handleSearchToggle = () => {
    setIsSearchOpen((prev) => !prev);
    setSearchQuery("");
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full z-50 bg-white shadow-md px-4 sm:px-6 py-2 flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          {isLoggedIn && (
            <button className="lg:hidden" onClick={toggleSidebar}>
              <GiHamburgerMenu size={24} />
            </button>
          )}
          <Link to="/" className="text-lg sm:text-xl font-bold text-gray-800">
            Dance.AI
          </Link>
        </div>

        {/* Search - Hidden on small until toggled */}
        <div className="hidden md:flex flex-1 justify-center">
          <div className="relative w-full max-w-md">
            <div className="flex items-center bg-gray-100 px-3 py-2 rounded-full">
              <FaSearch className="text-gray-500 mr-2" />
              <input
                type="text"
                className="w-full bg-transparent outline-none text-gray-800"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {filteredUsers.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center p-2 cursor-pointer hover:bg-gray-200 rounded-md"
                    onClick={() => {
                      navigate(`/profile/${user.uid}`);
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                  >
                    <img
                      src={user.profileImage || "https://via.placeholder.com/150"}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover mr-3"
                    />
                    <p className="font-semibold text-gray-800 text-sm">
                      {user.username}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile Search Button */}
          <button
            className="md:hidden p-2 rounded-full hover:bg-gray-100"
            onClick={handleSearchToggle}
          >
            <FaSearch size={18} />
          </button>

          {/* Chat */}
          {isLoggedIn && (
            <div className="hidden sm:block">
              <ChatButton onOpenChat={() => setShowChatModal(true)} />
            </div>
          )}

          {/* Contact */}
          <button
            onClick={() =>
              window.open(
                "mailto:stepwisedance@gmail.com?subject=Contact Dance.AI",
                "_blank"
              )
            }
            className="hidden sm:flex px-3 py-2 rounded-full border border-gray-200 hover:bg-gray-300 transition-all items-center"
          >
            Contact
          </button>

          {/* Upload */}
          {isLoggedIn && (
            <button
              className="hidden sm:block p-2 rounded-full hover:bg-gray-100"
              onClick={() => setShowUploadPopUp(true)}
            >
              <FaPlus size={20} />
            </button>
          )}

          {/* Auth */}
          {isLoggedIn ? (
            <ProfileIcon />
          ) : (
            <>
              <button
                className="hidden sm:block px-3 py-2 rounded-full border border-gray-200 hover:bg-gray-300 transition-all"
                onClick={() => setShowLoginPopup(true)}
              >
                Login
              </button>
              <button
                className="hidden sm:block px-3 py-2 rounded-full bg-violet-500 text-white hover:bg-violet-700 transition-all"
                onClick={() => setShowSignUpPopUp(true)}
              >
                Sign Up
              </button>
            </>
          )}

          {/* Mobile Menu */}
          <button
            className="lg:hidden p-2 rounded-full hover:bg-gray-100"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <GiHamburgerMenu size={22} />
          </button>
        </div>
      </nav>

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="md:hidden p-4 bg-white fixed top-12 left-0 w-full shadow-md z-40">
          <div className="flex items-center bg-gray-100 px-3 py-2 rounded-full">
            <FaSearch className="text-gray-500 mr-2" />
            <input
              type="text"
              className="w-full bg-transparent outline-none"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button onClick={handleSearchToggle}>
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu Drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed top-12 right-0 w-3/4 max-w-sm bg-white shadow-lg z-40 p-4 space-y-4">
          {isLoggedIn && (
            <>
              <button
                className="block w-full px-4 py-2 text-left hover:bg-gray-100 rounded"
                onClick={() => setShowUploadPopUp(true)}
              >
                Upload Video
              </button>
              <ChatButton onOpenChat={() => setShowChatModal(true)} />
            </>
          )}
          <button
            onClick={() =>
              window.open(
                "mailto:stepwisedance@gmail.com?subject=Contact Dance.AI",
                "_blank"
              )
            }
            className="block w-full px-4 py-2 text-left hover:bg-gray-100 rounded"
          >
            Contact
          </button>
          {!isLoggedIn && (
            <>
              <button
                className="block w-full px-4 py-2 text-left hover:bg-gray-100 rounded"
                onClick={() => setShowLoginPopup(true)}
              >
                Login
              </button>
              <button
                className="block w-full px-4 py-2 text-left hover:bg-violet-500 text-white rounded"
                onClick={() => setShowSignUpPopUp(true)}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      )}

      {/* Popups */}
      <ChatModal isOpen={showChatModal} onClose={() => setShowChatModal(false)} />
      {showLoginPopup && (
        <LoginPopUp
          showPopUp={showLoginPopup}
          onClose={() => setShowLoginPopup(false)}
        />
      )}
      {showSignUpPopUp && (
        <SignUpPopUp
          showPopUp={showSignUpPopUp}
          onClose={() => setShowSignUpPopUp(false)}
        />
      )}
      {showUploadPopUp && (
        <UploadVideoPopUp
          showPopUp={showUploadPopUp}
          onClose={() => setShowUploadPopUp(false)}
        />
      )}
    </>
  );
};

export default Navbar;
